import * as FileUtils from '../../../utils/file/file-utils';
import { APEER_BLOB_STORAGE_URL_PART } from '../../shared/constants/constants';
import { FileDatasetValidationStatus } from '../types';
import { FileSegmentationValidationStatus } from '../types/file-segmentation-validation-statuses.type';
import { FileStorageType } from '../types/file-storage-type.type';
import { FileMetadataModel } from './file-metadata-model.model';

export class FileModel {
    id: string;
    private _name: string;
    private _token: string;

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
        this.setTypeIfNull(value);
    }

    get token(): string {
        return this._token;
    }

    set token(value: string) {
        this._token = value;
        this.setTypeIfNull(value);
    }

    fullpath?: string;
    originalFileId?: string;
    sizeInBytes: number;
    classes: string[];
    uploadToken?: string;
    type?: string;
    parentId?: string;
    createdAt?: string;
    viewerUrl?: string;
    shareUrl?: string;
    workflowRunId?: string;
    workflowResultMetadata?: FileWorkflowResultMetadata;
    storageType?: FileStorageType = 'Azure';
    url?: string;
    uploadStatus?: 'Pending' | 'Finished';
    metadata: FileMetadataModel;
    convertStatus?: CziConversionStatus;
    validationStatus?: FileDatasetValidationStatus | FileSegmentationValidationStatus;

    constructor(
        id?: string,
        token?: string,
        createdAt?: string,
        storageType?: FileStorageType,
        sizeInBytes?: number,
        name?: string,
        fullpath?: string,
        type?: string,
    ) {
        if (id != null) {
            this.id = id;
        }
        this.type = type;
        if (token != null) {
            this.token = token;
        }

        this.fullpath = fullpath;
        this.createdAt = createdAt;
        this.storageType = storageType;
        if (sizeInBytes != null) {
            this.sizeInBytes = sizeInBytes;
        }

        if (!name && token != null) {
            this.name = FileUtils.getFileNameWithExtension(token);
        } else {
            this.name = name || '';
        }
        this.classes = [];
        this.metadata = new FileMetadataModel();
    }

    private setTypeIfNull(value: string): void {
        if (this.type == null) {
            this.type = FileUtils.getFileExtension(value);
        }
    }

    static createInstance(obj: {
        id?: string;
        name?: string;
        type?: string;
        token?: string;
        fullpath?: string;
        createdAt?: string;
        sizeInBytes?: number;
        storageType?: FileStorageType;
        viewerUrl?: string;
        convertStatus?: CziConversionStatus;
        metadata?: FileMetadataModel;
        validationStatus?: FileDatasetValidationStatus | FileSegmentationValidationStatus;
    }): FileModel {
        const file = new FileModel(obj.id, obj.token, obj.createdAt, obj.storageType || 'Azure', obj.sizeInBytes, obj.name, obj.fullpath);

        if (obj.viewerUrl) {
            file.viewerUrl = obj.viewerUrl;
        }
        if (obj.convertStatus) {
            file.convertStatus = obj.convertStatus;
        }
        if (obj.validationStatus) {
            file.validationStatus = obj.validationStatus;
        }
        if (obj.metadata) {
            file.metadata = obj.metadata;
        }

        return file;
    }

    static fromWebUrl(url: string): FileModel {
        const file = new FileModel();
        file.storageType = url.includes(APEER_BLOB_STORAGE_URL_PART) ? 'Azure' : 'Web';
        file.token = url;
        file.url = url;
        file.name = FileUtils.getFileNameWithExtension(url);
        return file;
    }

    static isApeerFile(url?: string, userId?: string): boolean {
        return url != null && url.includes(APEER_BLOB_STORAGE_URL_PART) && (userId == null || url.includes(userId));
    }

    static isResultFile(url: string): boolean {
        return url.includes(APEER_BLOB_STORAGE_URL_PART) && url.includes('/experiment/') && url.includes('/module');
    }
}

export interface FileAddingDtoPost {
    fileId: string;
    fileName: string;
    isAnnotation: boolean | undefined;
}

export interface FileAddingResponseDto {
    fileId: string;
    fileName: string;
    sizeInBytes: number;
    createdAt: string;
    isAnnotation: boolean | undefined;
    token: string;
    error?: string;
}

export interface FileDeleteResponseDto {
    fileId: string;
    fileName: string;
    error?: string;
}

export interface FileWorkflowResultMetadata {
    workflowInstanceId?: string;
    moduleId?: string;
    workflowModuleId?: string;
    moduleStep?: string;
}

export type CziConversionStatus = 'Success' | 'Active' | 'Failed' | 'Unknown' | 'NotCZI';
